import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  Hidden,
  TextField,
  Typography,
  Paper,
  Box,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Link,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useHistory, useLocation } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { useCookies } from 'react-cookie';
import * as Yup from 'yup';

import './styleForm.css';
import './style.css';
import axios from 'axios';
import StoreContext from '../../Store/context';
import useLogin from '../../../utils/useLogin';
import UILogo from '../../UI/Logo/UILogo';
import { useFirebaseAuth } from '../../../contexts/FirebaseAuthContext';
import { getHubApiUrl } from '../../../utils/hubApi';
import CompanySelectionModal from './CompanySelectionModal';

const adminValidationSchema = Yup.object().shape({
  email: Yup.string().email('E-mail inválido').required('Campo obrigatório'),
  password: Yup.string().required('Campo obrigatório'),
});

const systemUserValidationSchema = Yup.object().shape({
  username: Yup.string().required('Campo obrigatório'),
  password: Yup.string().required('Campo obrigatório'),
  company: Yup.string().required('Campo obrigatório'),
});

const LoginForm = () => {
  const { setCredencials } = useContext(StoreContext);
  const [cookies, setCookie] = useCookies(['company', 'last_login_type']);
  const history = useHistory();
  const location = useLocation();
  const { loginWithGoogle, loginWithEmail } = useFirebaseAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [adminAuthLoading, setAdminAuthLoading] = useState(false);
  const [adminAuthError, setAdminAuthError] = useState('');

  // Multi-Company Selection Modal State
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [userCompanies, setUserCompanies] = useState([]);
  const [selectingCompanyLoading, setSelectingCompanyLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const authFirebaseUserRef = useRef(null);

  // false: Admin (Left), true: System User / Operador (Right)
  const [isRightPanelActive, setIsRightPanelActive] = useState(() => {
    return cookies.last_login_type !== 'admin';
  });

  useEffect(() => {
    if (cookies.last_login_type) {
      setIsRightPanelActive(cookies.last_login_type !== 'admin');
    }
  }, [cookies.last_login_type]);

  const handleTogglePanel = (isUserPanel) => {
    setIsRightPanelActive(isUserPanel);
    setCookie('last_login_type', isUserPanel ? 'user' : 'admin', { path: '/' });
  };

  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [companyHistory, setCompanyHistory] = useState([]);
  const formikAdminRef = useRef(null);
  const formikUserRef = useRef(null);
  const currentAttemptId = useRef('');

  useEffect(() => {
    const saved = localStorage.getItem('duzz_company_history');
    if (saved) {
      try {
        setCompanyHistory(JSON.parse(saved));
      } catch (e) {
        // Ignora erro de parse
      }
    }
  }, []);

  useEffect(() => {
    if (cookies.company && formikUserRef.current && isRightPanelActive) {
      formikUserRef.current.setFieldValue('company', cookies.company);
      currentAttemptId.current = cookies.company;
    }
  }, [cookies.company, isRightPanelActive]);

  const hasAttemptedTokenLogin = useRef(false);

  const { login, loginWithToken, signInInfo } = useLogin({
    onCompleted: (data) => {
      const companyName =
        data.companyData?.pseudonimo || data.companyData?.name || 'Empresa';
      const companyId = data.companyData?.id || currentAttemptId.current;

      if (companyId) {
        setCookie('company', companyId, { path: '/' });
        updateHistory(companyId, companyName);
      }

      history.replace('/home');
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('loginToken');
    if (token && !hasAttemptedTokenLogin.current) {
      hasAttemptedTokenLogin.current = true;
      // Remove o token da barra de endereço imediatamente para evitar vazamento em histórico e referer
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        // Ignora erro de histórico
      }
      setCredencials(null);
      loginWithToken(token);
    }
  }, [location.search, loginWithToken, setCredencials]);

  const updateHistory = (id, name) => {
    if (!id) return;
    let newHistory = [...companyHistory];
    newHistory = newHistory.filter((item) => String(item.id) !== String(id));
    newHistory.unshift({ id: String(id), name: name });
    if (newHistory.length > 5) newHistory = newHistory.slice(0, 5);
    setCompanyHistory(newHistory);
    localStorage.setItem('duzz_company_history', JSON.stringify(newHistory));
  };

  const doGenerateTokenAndLogin = async (accountId, userParam) => {
    const user = userParam || authFirebaseUserRef.current;
    if (!user) return;
    const token = await user.getIdToken();
    const apiUrl = getHubApiUrl();

    const response = await axios.post(
      `${apiUrl}/auth/generate-token`,
      { account_id: accountId ? String(accountId) : null },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const loginToken = response.data?.token;
    if (loginToken) {
      setShowCompanyModal(false);
      loginWithToken(loginToken);
    } else {
      throw new Error('Não foi possível gerar o token de acesso da empresa.');
    }
  };

  const handleSelectCompany = async (company) => {
    try {
      setSelectedCompanyId(company.id);
      setSelectingCompanyLoading(true);
      await doGenerateTokenAndLogin(company.id);
    } catch (err) {
      console.error('Error selecting company:', err);
      setAdminAuthError(err.response?.data?.detail || err.message || 'Falha ao acessar empresa selecionada.');
      setShowCompanyModal(false);
    } finally {
      setSelectingCompanyLoading(false);
      setSelectedCompanyId(null);
    }
  };

  const handleFirebaseUserLogin = async (user) => {
    try {
      setCookie('last_login_type', 'admin', { path: '/' });
      authFirebaseUserRef.current = user;
      const token = await user.getIdToken();
      const apiUrl = getHubApiUrl();

      // Fetch all companies registered to this admin
      let companies = [];
      try {
        const companiesRes = await axios.get(`${apiUrl}/auth/my-companies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        companies = (companiesRes.data?.companies || []).filter(
          (c) => Boolean(c.production_company_id || c.has_production)
        );
      } catch (eComp) {
        console.warn('Could not fetch company list, falling back to single account login:', eComp);
      }

      if (companies.length === 0) {
        // Direct attempt with generate-token or redirect to onboarding
        await doGenerateTokenAndLogin(cookies.company ? String(cookies.company) : null, user);
        return;
      }

      if (companies.length === 1) {
        // Only 1 company: login directly
        await doGenerateTokenAndLogin(companies[0].id, user);
        return;
      }

      // Multiple companies: open selection modal
      setUserCompanies(companies);
      setShowCompanyModal(true);
    } catch (err) {
      if (err.response?.status === 404) {
        // Nenhuma empresa associada ainda -> redireciona para onboarding
        history.push('/onboarding');
        return;
      }
      throw err;
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setGoogleLoading(true);
      setAdminAuthError('');
      setCookie('last_login_type', 'admin', { path: '/' });
      const googleRes = await loginWithGoogle();
      const user = googleRes?.user;
      if (!user) {
        throw new Error('Não foi possível obter dados da conta Google.');
      }
      await handleFirebaseUserLogin(user);
    } catch (err) {
      console.error('Google Auth Error:', err);
      setAdminAuthError(err.message || 'Falha ao autenticar com o Google.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const onSubmitAdmin = async (values) => {
    try {
      setAdminAuthLoading(true);
      setAdminAuthError('');
      setCookie('last_login_type', 'admin', { path: '/' });
      const authRes = await loginWithEmail(values.email, values.password);
      const user = authRes?.user;
      if (!user) {
        throw new Error('Não foi possível autenticar o usuário.');
      }
      await handleFirebaseUserLogin(user);
    } catch (err) {
      console.error('Admin Auth Error:', err);
      let errorMsg = 'Falha ao autenticar administrador.';
      if (
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential'
      ) {
        errorMsg = 'E-mail ou senha incorretos.';
      } else if (err.code === 'auth/invalid-email') {
        errorMsg = 'E-mail inválido.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMsg = 'Muitas tentativas sem sucesso. Tente novamente mais tarde.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setAdminAuthError(errorMsg);
    } finally {
      setAdminAuthLoading(false);
    }
  };

  function onSubmitUser(values) {
    setCookie('last_login_type', 'user', { path: '/' });
    setCookie('company', values.company, { path: '/' });
    currentAttemptId.current = values.company;
    login(values);
  }

  return (
    <div className="user-login__global">
      <Container maxWidth="lg" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Grid
          className="user-login__top-nav"
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <Hidden xsDown>
            <UILogo className="user-login__duzz-commercial" size="32px" />
          </Hidden>
          <Hidden smUp>
            <div />
          </Hidden>
          <Button
            className="user-login__btn-adquira"
            variant="outlined"
            color="primary"
            onClick={() => history.push('/onboarding')}
            style={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}
          >
            Criar conta ou testar grátis
          </Button>
        </Grid>

        <Grid container alignItems="center" justify="center" style={{ flexGrow: 1, padding: '20px 0' }}>
          {isMobile ? (
            /* Mobile View with Tabs */
            <Paper elevation={3} style={{ width: '100%', padding: '20px', borderRadius: '16px' }}>
              <Tabs
                value={isRightPanelActive ? 1 : 0}
                onChange={(e, val) => handleTogglePanel(val === 1)}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                style={{ marginBottom: '20px' }}
              >
                <Tab label="Entrar como Admin" style={{ fontWeight: 600 }} />
                <Tab label="Usuário do Sistema" style={{ fontWeight: 600 }} />
              </Tabs>

              {!isRightPanelActive ? (
                /* Admin Form Mobile */
                <Formik
                  innerRef={formikAdminRef}
                  initialValues={{ email: '', password: '' }}
                  validationSchema={adminValidationSchema}
                  onSubmit={onSubmitAdmin}
                >
                  {({ errors, touched }) => (
                    <Form>
                      <Typography variant="h6" color="primary" style={{ fontWeight: 700, marginBottom: '8px' }}>
                        Entrar como Administrador
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                        Acesse a gestão com seu e-mail e senha
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={handleGoogleAuth}
                        disabled={googleLoading}
                        style={{
                          borderColor: '#dadce0',
                          backgroundColor: '#ffffff',
                          color: '#3c4043',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          marginBottom: '12px',
                        }}
                        startIcon={
                          <svg width="18" height="18" viewBox="0 0 18 18">
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.617z" />
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
                            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                          </svg>
                        }
                      >
                        {googleLoading ? 'Entrando...' : 'Entrar com Google'}
                      </Button>

                      <Box display="flex" alignItems="center" my={1.5}>
                        <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
                        <Typography variant="caption" color="textSecondary" style={{ padding: '0 12px', textTransform: 'uppercase' }}>
                          ou com e-mail
                        </Typography>
                        <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
                      </Box>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          name="email"
                          label="E-mail do Administrador"
                          variant="outlined"
                          placeholder="admin@suaempresa.com"
                          helperText={touched.email && errors.email}
                          error={touched.email && Boolean(errors.email)}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          label="Senha"
                          name="password"
                          variant="outlined"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Sua senha"
                          helperText={touched.password && errors.password}
                          error={touched.password && Boolean(errors.password)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormControl>

                      {(adminAuthError || signInInfo.error) && (
                        <Box mt={2} p={1.5} bgcolor="#FEF2F2" borderRadius="8px" border="1px solid #FCA5A5">
                          <Typography variant="body2" style={{ color: "#991B1B", fontWeight: 600 }}>
                            {adminAuthError ? `⚠️ ${adminAuthError}` : "⚠️ Falha ao entrar. Verifique os dados ou "}
                            {!adminAuthError && (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (formikAdminRef.current) formikAdminRef.current.setFieldValue('password', '');
                                  if (formikUserRef.current) formikUserRef.current.setFieldValue('password', '');
                                  history.push("/onboarding");
                                }}
                                style={{ color: "#17497F", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                              >
                                crie uma conta aqui
                              </Link>
                            )}
                          </Typography>
                        </Box>
                      )}

                      <Box mt={3}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={adminAuthLoading || signInInfo.loading}
                          style={{ height: '48px', fontWeight: 'bold', borderRadius: '8px' }}
                        >
                          {adminAuthLoading || signInInfo.loading ? 'ENTRANDO...' : 'ENTRAR COMO ADMIN'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              ) : (
                /* User Form Mobile */
                <Formik
                  innerRef={formikUserRef}
                  initialValues={{ username: '', password: '', company: cookies.company || '' }}
                  validationSchema={systemUserValidationSchema}
                  onSubmit={onSubmitUser}
                >
                  {({ errors, touched, setFieldValue, values }) => (
                    <Form>
                      <Typography variant="h6" color="primary" style={{ fontWeight: 700, marginBottom: '8px' }}>
                        Usuário do Sistema
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                        Informe seu login, senha e código da empresa
                      </Typography>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          name="username"
                          label="Login / Usuário"
                          variant="outlined"
                          placeholder="Seu usuário"
                          helperText={touched.username && errors.username}
                          error={touched.username && Boolean(errors.username)}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          label="Senha"
                          name="password"
                          variant="outlined"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Sua senha"
                          helperText={touched.password && errors.password}
                          error={touched.password && Boolean(errors.password)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <Autocomplete
                          freeSolo
                          disableClearable
                          forcePopupIcon={true}
                          options={companyHistory}
                          getOptionLabel={(option) => (typeof option === 'string' ? option : String(option.id))}
                          inputValue={values.company !== undefined && values.company !== null ? String(values.company) : ''}
                          onInputChange={(e, val) => setFieldValue('company', val)}
                          onChange={(e, val) => {
                            if (val && typeof val === 'object') setFieldValue('company', val.id);
                          }}
                          renderOption={(option) => (
                            <Box display="flex" flexDirection="column" py={1}>
                              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Cód: {option.id}
                              </Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cód. Empresa"
                              variant="outlined"
                              placeholder="Digite ou selecione..."
                              error={touched.company && Boolean(errors.company)}
                              helperText={touched.company && errors.company}
                            />
                          )}
                        />
                      </FormControl>

                      {signInInfo.error && (
                        <Box mt={2} p={1.5} bgcolor="#FEF2F2" borderRadius="8px" border="1px solid #FCA5A5">
                          <Typography variant="body2" style={{ color: "#991B1B", fontWeight: 600 }}>
                            ⚠️ Falha ao entrar. Verifique os dados ou{" "}
                            <Link
                              component="button"
                              type="button"
                              variant="body2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (formikAdminRef.current) formikAdminRef.current.setFieldValue('password', '');
                                if (formikUserRef.current) formikUserRef.current.setFieldValue('password', '');
                                history.push("/onboarding");
                              }}
                              style={{ color: "#17497F", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                            >
                              crie uma conta aqui
                            </Link>
                          </Typography>
                        </Box>
                      )}

                      <Box mt={3}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={signInInfo.loading}
                          style={{ height: '48px', fontWeight: 'bold', borderRadius: '8px' }}
                        >
                          {signInInfo.loading ? 'ENTRANDO...' : 'ENTRAR COMO USUÁRIO'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}

              <Box mt={3} textAlign="center">
                <Button
                  color="primary"
                  onClick={() => history.push('/onboarding')}
                  style={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Criar nova conta ou testar gratuitamente →
                </Button>
              </Box>
            </Paper>
          ) : (
            /* Desktop Sliding Overlay Container (Inspired by Portal Developer Jamef / Classic Duzz) */
            <div className={`login-card-container ${isRightPanelActive ? 'right-panel-active' : ''}`}>
              {/* Form 1: Admin Container (Left Side) */}
              <div className="login-form-container admin-container">
                <Formik
                  innerRef={formikAdminRef}
                  initialValues={{ email: '', password: '' }}
                  validationSchema={adminValidationSchema}
                  onSubmit={onSubmitAdmin}
                >
                  {({ errors, touched }) => (
                    <Form style={{ width: '100%' }}>
                      <Typography variant="h5" color="primary" style={{ fontWeight: 800, marginBottom: '8px' }}>
                        Entrar como Admin
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                        Utilize seu e-mail cadastrado de administrador
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={handleGoogleAuth}
                        disabled={googleLoading}
                        style={{
                          borderColor: '#dadce0',
                          backgroundColor: '#ffffff',
                          color: '#3c4043',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          marginBottom: '12px',
                        }}
                        startIcon={
                          <svg width="18" height="18" viewBox="0 0 18 18">
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.617z" />
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
                            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                          </svg>
                        }
                      >
                        {googleLoading ? 'Entrando...' : 'Entrar com Google'}
                      </Button>

                      <Box display="flex" alignItems="center" my={1.5}>
                        <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
                        <Typography variant="caption" color="textSecondary" style={{ padding: '0 12px', textTransform: 'uppercase' }}>
                          ou com e-mail
                        </Typography>
                        <Box flexGrow={1} height="1px" bgcolor="#e0e0e0" />
                      </Box>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          name="email"
                          label="E-mail do Administrador"
                          variant="outlined"
                          placeholder="admin@suaempresa.com"
                          helperText={touched.email && errors.email}
                          error={touched.email && Boolean(errors.email)}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="normal">
                        <Field
                          as={TextField}
                          label="Senha"
                          name="password"
                          variant="outlined"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Sua senha"
                          helperText={touched.password && errors.password}
                          error={touched.password && Boolean(errors.password)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormControl>

                      {(adminAuthError || signInInfo.error) && (
                        <Box mt={2} p={1.5} bgcolor="#FEF2F2" borderRadius="8px" border="1px solid #FCA5A5">
                          <Typography variant="body2" style={{ color: "#991B1B", fontWeight: 600 }}>
                            {adminAuthError ? `⚠️ ${adminAuthError}` : "⚠️ Falha ao entrar. Verifique os dados ou "}
                            {!adminAuthError && (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (formikAdminRef.current) formikAdminRef.current.setFieldValue('password', '');
                                  if (formikUserRef.current) formikUserRef.current.setFieldValue('password', '');
                                  history.push("/onboarding");
                                }}
                                style={{ color: "#17497F", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                              >
                                crie uma conta aqui
                              </Link>
                            )}
                          </Typography>
                        </Box>
                      )}

                      <Box mt={4}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={adminAuthLoading || signInInfo.loading}
                          style={{
                            height: '50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(23,73,127,0.25)',
                          }}
                          startIcon={(adminAuthLoading || signInInfo.loading) && <CircularProgress size="1rem" color="inherit" />}
                        >
                          {adminAuthLoading || signInInfo.loading ? 'ENTRANDO...' : 'ENTRAR COMO ADMIN'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </div>

              {/* Form 2: System User Container (Right Side) */}
              <div className="login-form-container user-container">
                <Formik
                  innerRef={formikUserRef}
                  initialValues={{ username: '', password: '', company: cookies.company || '' }}
                  validationSchema={systemUserValidationSchema}
                  onSubmit={onSubmitUser}
                >
                  {({ errors, touched, setFieldValue, values }) => (
                    <Form style={{ width: '100%' }}>
                      <Typography variant="h5" color="primary" style={{ fontWeight: 800, marginBottom: '8px' }}>
                        Usuário do Sistema
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '20px' }}>
                        Informe seu login, senha e o código da empresa
                      </Typography>

                      <FormControl fullWidth margin="dense">
                        <Field
                          as={TextField}
                          name="username"
                          label="Login / Usuário"
                          variant="outlined"
                          placeholder="Seu usuário"
                          helperText={touched.username && errors.username}
                          error={touched.username && Boolean(errors.username)}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="dense">
                        <Field
                          as={TextField}
                          label="Senha"
                          name="password"
                          variant="outlined"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Sua senha"
                          helperText={touched.password && errors.password}
                          error={touched.password && Boolean(errors.password)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormControl>

                      <FormControl fullWidth margin="dense">
                        <Autocomplete
                          freeSolo
                          disableClearable
                          forcePopupIcon={true}
                          options={companyHistory}
                          getOptionLabel={(option) => (typeof option === 'string' ? option : String(option.id))}
                          inputValue={values.company !== undefined && values.company !== null ? String(values.company) : ''}
                          onInputChange={(e, val) => setFieldValue('company', val)}
                          onChange={(e, val) => {
                            if (val && typeof val === 'object') setFieldValue('company', val.id);
                          }}
                          renderOption={(option) => (
                            <Box display="flex" flexDirection="column" py={1}>
                              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Cód: {option.id}
                              </Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cód. Empresa"
                              variant="outlined"
                              placeholder="Digite ou selecione..."
                              error={touched.company && Boolean(errors.company)}
                              helperText={touched.company && errors.company}
                            />
                          )}
                        />
                      </FormControl>

                      {signInInfo.error && (
                        <Box mt={1} p={1.5} bgcolor="#FEF2F2" borderRadius="8px" border="1px solid #FCA5A5">
                          <Typography variant="body2" style={{ color: "#991B1B", fontWeight: 600 }}>
                            ⚠️ Falha ao entrar. Verifique os dados ou{" "}
                            <Link
                              component="button"
                              type="button"
                              variant="body2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (formikAdminRef.current) formikAdminRef.current.setFieldValue('password', '');
                                if (formikUserRef.current) formikUserRef.current.setFieldValue('password', '');
                                history.push("/onboarding");
                              }}
                              style={{ color: "#17497F", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}
                            >
                              crie uma conta aqui
                            </Link>
                          </Typography>
                        </Box>
                      )}

                      <Box mt={3}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          disabled={signInInfo.loading}
                          style={{
                            height: '50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(23,73,127,0.25)',
                          }}
                          startIcon={signInInfo.loading && <CircularProgress size="1rem" color="inherit" />}
                        >
                          {signInInfo.loading ? 'ENTRANDO...' : 'ENTRAR COMO USUÁRIO'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </div>

              {/* Sliding Overlay Container */}
              <div className="overlay-container">
                <div className="overlay">
                  {/* Left Overlay: Visible when Right Panel Active (System User active) */}
                  <div className="overlay-panel overlay-left">
                    <img src="/image/Alinhado.png" alt="Duzz Commercial" className="overlay-logo-img" />
                    <Typography variant="h4" style={{ fontWeight: 800, marginBottom: '12px' }}>
                      É Administrador?
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.9, lineHeight: 1.6, marginBottom: '20px' }}>
                      Acesse a gestão completa utilizando seu e-mail cadastrado e senha.
                    </Typography>
                    <button
                      className="ghost-button"
                      onClick={() => handleTogglePanel(false)}
                    >
                      Entrar como Admin
                    </button>
                    <Box mt={3}>
                      <Button
                        style={{ color: '#fff', textTransform: 'none', fontSize: '13px', opacity: 0.9, fontWeight: 500 }}
                        onClick={() => history.push('/onboarding')}
                      >
                        Criar nova conta ou testar grátis →
                      </Button>
                    </Box>
                  </div>

                  {/* Right Overlay: Visible when Left Panel Active (Admin active) */}
                  <div className="overlay-panel overlay-right">
                    <img src="/image/Alinhado.png" alt="Duzz Commercial" className="overlay-logo-img" />
                    <Typography variant="h4" style={{ fontWeight: 800, marginBottom: '12px' }}>
                      Usuário do Sistema?
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.9, lineHeight: 1.6, marginBottom: '20px' }}>
                      Se você é colaborador, acesse com seu nome de usuário, senha e código da empresa.
                    </Typography>
                    <button
                      className="ghost-button"
                      onClick={() => handleTogglePanel(true)}
                    >
                      Entrar como Usuário
                    </button>
                    <Box mt={3}>
                      <Button
                        style={{ color: '#fff', textTransform: 'none', fontSize: '13px', opacity: 0.9, fontWeight: 500 }}
                        onClick={() => history.push('/onboarding')}
                      >
                        Criar nova conta ou testar grátis →
                      </Button>
                    </Box>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Grid>
      </Container>

      {/* Multi-Company Selection Modal for Admin Users */}
      <CompanySelectionModal
        open={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companies={userCompanies}
        onSelectCompany={handleSelectCompany}
        onCreateNewCompany={() => {
          setShowCompanyModal(false);
          history.push('/onboarding');
        }}
        loading={selectingCompanyLoading}
        selectedCompanyId={selectedCompanyId}
      />
    </div>
  );
};

export default LoginForm;
